import type { PropertyDTO } from "@zplanem/responses"
import { useEffect, useRef, useState } from "react"
import { cn } from "../../../lib/utils"
import { useUpdateProperty } from "../../hooks/use-update-property"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

const TITLE_CLASS = "min-w-0 text-5xl font-black text-foreground tracking-tight"

/**
 * The property name as an `<h1>` that the owner can edit in place (ZPL-186):
 * hover shows a text cursor, click swaps to an input, Enter/blur saves, Esc
 * cancels. Non-owners just see the heading.
 */
export function EditablePropertyTitle({
	property,
	canEdit,
}: {
	property: PropertyDTO
	canEdit: boolean
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.edit
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(property.name)
	const inputRef = useRef<HTMLInputElement>(null)
	const mutation = useUpdateProperty(property.id)

	useEffect(() => {
		if (editing) inputRef.current?.focus()
	}, [editing])

	function start() {
		setDraft(property.name)
		setEditing(true)
	}

	async function save() {
		const name = draft.trim()
		if (!name || name === property.name) {
			setEditing(false)
			return
		}
		try {
			await mutation.mutateAsync({ name })
		} finally {
			setEditing(false)
		}
	}

	if (canEdit && editing) {
		return (
			<input
				ref={inputRef}
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onBlur={save}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault()
						save()
					} else if (e.key === "Escape") {
						setEditing(false)
					}
				}}
				disabled={mutation.isPending}
				aria-label={t.editName}
				className={cn(
					TITLE_CLASS,
					"w-full bg-transparent border-b border-border outline-none focus:border-ring disabled:opacity-60",
				)}
			/>
		)
	}

	if (!canEdit) return <h1 className={TITLE_CLASS}>{property.name}</h1>

	return (
		<h1
			className={cn(TITLE_CLASS, "cursor-text")}
			title={t.editName}
			onClick={start}
			onKeyDown={(e) => {
				if (e.key === "Enter") start()
			}}
		>
			{property.name}
		</h1>
	)
}
